//RichTextEditor:
import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { Box } from '@mui/material';
import RichTextEditorToolbar2 from './RichTextEditorToolbar2';
import { useFieldMetadata } from '../../contexts/FieldMetadataContext';
import { useFormSubmissionSettings } from '../../contexts/FormSubmissionSettingsContext';

// Utility functions to map font sizes between px and HTML scale
const pxToHtmlFontSize = {
  '12px': 1,
  '16px': 2,
  '24px': 3,
  '32px': 4,
  '48px': 5,
  '64px': 6,
  '96px': 7,
};

const RichTextEditor2 = ({ value, onChange, placeholder = 'Type your submit message here...', styles }) => {
  const editorRef = useRef(null);
  const editorContainerRef = useRef(null); // NEW: Ref for the entire editor container
  const { updateSettings } = useFormSubmissionSettings();
  const [isEmpty, setIsEmpty] = useState(true); // Track if editor is empty

  const [activeStyles, setActiveStyles] = useState({
    bold: false,
    italic: false,
    underline: false,
    textAlign: styles?.textAlign || 'left',
    fontSize: styles?.fontSize || '16px',
    fontFamily: styles?.fontFamily || 'Arial',
    fontColor: styles?.textColor || '#000000',
  });

// NEW: Memoize a mapping from the unique field identifier (ufi if available, otherwise field id)
// to the field's label. This ensures we update chip labels only when the field metadata changes.
const { fieldsMetadata } = useFieldMetadata();
const fieldIdToLabelMap = useMemo(() => {
  const mapping = {};
  Object.keys(fieldsMetadata).forEach((fieldKey) => {
    const fieldData = fieldsMetadata[fieldKey];
    if (fieldData) {
      // Use the field's ufi if available; otherwise, fallback to the fieldKey.
      const uniqueKey = fieldData.ufi || fieldKey;
      mapping[uniqueKey] = {
        label: fieldData.label || `Field ${fieldKey}`,
      };
    }
  });
  console.log('[RichTextEditor2] Memoized fieldIdToLabelMap:', mapping);
  return mapping;
}, [fieldsMetadata]);


  // Sync initial styles and value into the editor
  useEffect(() => {
    if (editorRef.current) {
      if (value !== editorRef.current.innerHTML) {
        editorRef.current.innerHTML = value || '';
      }
      setIsEmpty(!value); // Update placeholder visibility
      setActiveStyles((prev) => ({
        ...prev,
        textAlign: styles?.textAlign || 'left',
        fontSize: styles?.fontSize || '16px',
        fontFamily: styles?.fontFamily || 'Arial',
        fontColor: styles?.textColor || '#000000',
      }));
    }
  }, [value, styles]);

// NEW: On mount (or when the field mapping changes), update chip labels in the editor.
// This effect queries all elements with the attribute data-field-id and updates their text
// to match the current label from the memoized fieldIdToLabelMap.
useEffect(() => {
  if (editorRef.current) {
    const chips = editorRef.current.querySelectorAll('[data-field-id]');
    chips.forEach((chip) => {
      const chipId = chip.getAttribute('data-field-id');
      if (chipId && fieldIdToLabelMap[chipId]) {
        const currentLabel = chip.textContent;
        const newLabel = fieldIdToLabelMap[chipId].label;
        if (currentLabel !== newLabel) {
          console.log(`[RichTextEditor2] Updating chip for field ${chipId} from "${currentLabel}" to "${newLabel}"`);
          chip.textContent = newLabel;
        }
      }
    });
  }
  // This effect runs on mount and whenever fieldIdToLabelMap changes.
}, [fieldIdToLabelMap]);

// NEW: Function to handle saving when the editor loses focus (click outside)
const handleEditorBlurSave = useCallback(() => {
  if (editorRef.current) {
    const currentHtml = editorRef.current.innerHTML;
//    console.log('[RichTextEditor2] Editor blurred. Current HTML content to be saved:', currentHtml);
    // Update the context's displayInCard action by storing the HTML content in the customMessage property.
    updateSettings('displayInCard', { customMessage: currentHtml });
//    console.log('[RichTextEditor2] updateSettings called for displayInCard with customMessage.');
  }
}, [updateSettings]);


// NEW: Outside click detection effect to trigger save on editor blur
useEffect(() => {
  const handleClickOutside = (event) => {
    if (editorContainerRef.current && !editorContainerRef.current.contains(event.target)) {
//      console.log('[RichTextEditor2] Outside click detected. Triggering editor blur save.');
      handleEditorBlurSave();
    }
  };

  document.addEventListener('mousedown', handleClickOutside);
  return () => {
    document.removeEventListener('mousedown', handleClickOutside);
  };
}, [handleEditorBlurSave]);



  // Function to handle style changes
const handleStyleChange = useCallback(
  (key, value) => {
    if (editorRef.current) {
      editorRef.current.focus();
    }

    switch (key) {
      case 'bold':
      case 'italic':
      case 'underline':
        document.execCommand(key);
        break;
      case 'textAlign':
        document.execCommand('justify' + value.charAt(0).toUpperCase() + value.slice(1));
        break;
      case 'fontSize': {
        const htmlSize = pxToHtmlFontSize[value] || 3;
        document.execCommand('fontSize', false, htmlSize);
        const fontElements = editorRef.current.getElementsByTagName('font');
        Array.from(fontElements).forEach((font) => {
          if (font.size === `${htmlSize}`) {
            font.removeAttribute('size');
            font.style.fontSize = value;
          }
        });
        break;
      }
      case 'fontFamily':
        document.execCommand('fontName', false, value);
        break;
      case 'fontColor':
        document.execCommand('foreColor', false, value);
        break;

case 'insertCustomChip':
  if (value && editorRef.current) {
    const chip = document.createElement('span'); // Create an inline span for the chip
    chip.textContent = value.label; // ✅ Display field label
    chip.setAttribute('data-field-id', value.value); // ✅ Store unique ID
    
    chip.setAttribute('draggable', 'true'); // ✅ Enable dragging
    chip.contentEditable = 'false'; // Prevent text editing inside the chip
    chip.tabIndex = 0; // Make the chip focusable for keyboard events

    // ✅ Apply Dynamic Styles Based on activeStyles
    chip.style.display = 'inline-block';
    chip.style.padding = '2px 8px';
    chip.style.backgroundColor = '#1976d2';
    chip.style.borderRadius = '16px';
    chip.style.margin = '2px';
    chip.style.cursor = 'move'; // Indicate it's draggable
    chip.style.userSelect = 'none';

    // 🔑 Dynamically Inherit Styles from activeStyles
    chip.style.fontFamily = activeStyles.fontFamily || 'Arial';
    chip.style.fontSize = activeStyles.fontSize || '16px';
    chip.style.color = activeStyles.fontColor || '#fff';

    let isDragging = false; // ✅ Track dragging status
    let draggedChip = null; // ✅ Store the dragged chip reference

    // ✅ Handle Drag Start
    chip.addEventListener('dragstart', (e) => {
      isDragging = true;
      draggedChip = chip; // Store the chip being dragged
      e.dataTransfer.setData('text/plain', chip.textContent); // Required for Firefox
      e.dataTransfer.effectAllowed = 'move';
    chip.style.opacity = '0.3';

    });

    // ✅ Handle Drag End
    chip.addEventListener('dragend', () => {
      isDragging = false;
      draggedChip = null; // Clear the reference after drag ends
    chip.style.opacity = '1';

    });

    // ✅ Allow Deleting Chip with Backspace/Delete Keys
    chip.addEventListener('keydown', (e) => {
      if ((e.key === 'Backspace' || e.key === 'Delete') && !isDragging) {
        e.preventDefault();
        chip.remove();
      }
    });

    // ✅ Select Entire Chip on Click for Easy Moving/Deleting
    chip.addEventListener('click', (e) => {
      if (!isDragging) {
        const range = document.createRange();
        range.selectNode(chip);
        const selection = window.getSelection();
        selection.removeAllRanges();
        selection.addRange(range);
      }
    });

    // ✅ Handle Drag Over (Allow dropping)
    editorRef.current.addEventListener('dragover', (e) => {
      e.preventDefault(); // Necessary to allow drop
      e.dataTransfer.dropEffect = 'move';
    });

    // ✅ Handle Drop Event
    editorRef.current.addEventListener('drop', (e) => {
      e.preventDefault();
      if (draggedChip) {
        const selection = window.getSelection();
        const range = document.caretRangeFromPoint(e.clientX, e.clientY); // Determine drop location

        if (range) {
          range.insertNode(draggedChip); // Move the dragged chip to the drop location
          range.collapse(false); // Place the cursor after the chip
        }
      }
    });

    // ✅ Insert the Chip at the Current Cursor Position (initial placement)
    const selection = window.getSelection();
    if (selection && selection.rangeCount > 0) {
      const range = selection.getRangeAt(0);
      range.deleteContents();
      range.insertNode(chip);
      range.collapse(false);
    }
  }
  break;


      default:
        break;
    }

    setActiveStyles((prev) => ({ ...prev, [key]: value }));

    if (onChange) {
      onChange({
        text: editorRef.current.innerHTML,
        styles: { ...activeStyles, [key]: value },
      });
    }
  },
  [activeStyles, onChange]
);


  // Function to update toolbar state based on current selection
  const updateToolbarState = useCallback(() => {
    const selection = window.getSelection();
    if (!selection.rangeCount) return;

    const range = selection.getRangeAt(0);
    let node = range.startContainer;
    if (node.nodeType === Node.TEXT_NODE) {
      node = node.parentNode;
    }

    if (editorRef.current.contains(node)) {
      const computedStyle = window.getComputedStyle(node);
      setActiveStyles((prev) => ({
        ...prev,
        bold: computedStyle.fontWeight === 'bold' || parseInt(computedStyle.fontWeight, 10) >= 700,
        italic: computedStyle.fontStyle === 'italic',
        underline: computedStyle.textDecoration.includes('underline'),
        textAlign: computedStyle.textAlign || 'left',
    fontSize: Object.keys(pxToHtmlFontSize).find(key => pxToHtmlFontSize[key] === parseInt(computedStyle.fontSize, 10)) || prev.fontSize,
        fontFamily: computedStyle.fontFamily.split(',')[0].replace(/['"]/g, ''),
        fontColor: computedStyle.color || prev.fontColor || '#000000',
      }));
    }
  }, []);

  const handleInput = () => {
    const currentContent = editorRef.current.innerText.trim();
    setIsEmpty(currentContent === ''); // Check if editor is empty

    if (onChange) {
      onChange({
        text: editorRef.current.innerHTML,
        styles: activeStyles,
      });
    }
  };

  return (
    <Box
      ref={editorContainerRef}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 0,
      }}
    >
      {/* Toolbar */}
      <RichTextEditorToolbar2
        activeStyles={activeStyles}
        onStyleChange={handleStyleChange}
      />

      {/* Content Editor with Placeholder */}
      <Box
        sx={{
          position: 'relative', // Ensure placeholder stays inside the editor area
          border: 'none',
          borderColor: 'divider',
          borderRadius: 1,
          minHeight: 75,
          p: 1,
        }}
      >
        {/* Placeholder */}
        {isEmpty && (
          <Box
            sx={{
              position: 'absolute',
              top: '8px', // Adjusted to align with the cursor
              left: '8px',
              color: 'gray',
              opacity: 0.6,
              pointerEvents: 'none', // Allow clicking through to the editor
              fontStyle: 'italic',
              fontSize: activeStyles.fontSize,
              fontFamily: activeStyles.fontFamily,
              textAlign: activeStyles.textAlign,
            }}
          >
            {placeholder}
          </Box>
        )}

        {/* Editable Content Area */}
        <Box
          ref={editorRef}
          contentEditable
          suppressContentEditableWarning
          onInput={handleInput}
          onMouseUp={updateToolbarState}
          onKeyUp={updateToolbarState}
          sx={{
            outline: 'none',
            minHeight: '80px',
            whiteSpace: 'pre-wrap',
            wordWrap: 'break-word',
            textAlign: activeStyles.textAlign,
            color: activeStyles.fontColor,
            fontSize: activeStyles.fontSize,
            fontFamily: activeStyles.fontFamily,
          }}
        />
      </Box>
    </Box>
  );
};

export default RichTextEditor2;
